import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiFetch } from '../api';
import { loadStoredAuth } from '../authStorage';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { Skeleton } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/ToastProvider';
import { hasFrontendPermission } from '../permissions';

const makeEmptyDraft = () => ({
  name: '',
  email: '',
  phone: '',
  address: '',
});

const RefreshIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
    <path d="M11.5 6.5A5 5 0 1 1 9 2.1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M9 1v3h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PlusIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
    <path d="M6.5 2v9M2 6.5h9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

function CustomerStat({ label, value, tone = 'default' }) {
  return (
    <article className={`customers-stat customers-stat--${tone}`}>
      <span className="customers-stat-label">{label}</span>
      <strong className="customers-stat-value">{value}</strong>
    </article>
  );
}

function CustomerField({ label, children }) {
  return (
    <label className="customers-field">
      <span className="customers-field-label">{label}</span>
      {children}
    </label>
  );
}

function CustomerListItem({ customer, active, onSelect }) {
  const initials = String(customer.name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || '?';

  return (
    <button
      type="button"
      className={`customers-list-item${active ? ' is-active' : ''}`}
      onClick={() => onSelect(customer.id)}
    >
      <div className="customers-avatar">{initials}</div>
      <div className="customers-list-content">
        <strong className="customers-list-name">{customer.name || 'Unnamed customer'}</strong>
        <span className="customers-list-meta">{customer.email || 'No email'}</span>
        <span className="customers-list-meta">{customer.phone || 'No phone'}</span>
      </div>
    </button>
  );
}

export function CustomersPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { pushToast } = useToast();
  const auth = loadStoredAuth();
  const token = auth?.token;
  const user = auth?.user;
  const canManageCustomers = hasFrontendPermission(user, 'customers.manage');
  const canDeleteCustomers = hasFrontendPermission(user, 'customers.delete.any');

  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [draft, setDraft] = useState(makeEmptyDraft);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const createCardRef = useRef(null);
  const createNameInputRef = useRef(null);

  const closeCreateForm = useCallback(() => {
    setShowCreateForm(false);
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('compose') === '1') {
      navigate('/customers', { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    if (!token) navigate('/login');
  }, [token, navigate]);

  const loadCustomers = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch('/api/customers', { token });
      setCustomers(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers, refreshKey]);

  useEffect(() => {
    if (!canManageCustomers) return;
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('compose') !== '1') return;
    setShowCreateForm(true);
    window.requestAnimationFrame(() => {
      createCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      createNameInputRef.current?.focus();
    });
  }, [canManageCustomers, location.search]);

  const filteredCustomers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return customers;
    return customers.filter((customer) => (
      [customer.name, customer.email, customer.phone, customer.address]
        .some((value) => String(value || '').toLowerCase().includes(term))
    ));
  }, [customers, searchTerm]);

  useEffect(() => {
    if (!filteredCustomers.length) {
      setSelectedCustomerId('');
      return;
    }
    if (!filteredCustomers.some((customer) => customer.id === selectedCustomerId)) {
      setSelectedCustomerId(filteredCustomers[0].id);
    }
  }, [filteredCustomers, selectedCustomerId]);

  const selectedCustomer = useMemo(
    () => filteredCustomers.find((customer) => customer.id === selectedCustomerId) || null,
    [filteredCustomers, selectedCustomerId],
  );

  const stats = useMemo(() => ({
    total: customers.length,
    withEmail: customers.filter((customer) => customer.email).length,
    withPhone: customers.filter((customer) => customer.phone).length,
    withAddress: customers.filter((customer) => customer.address).length,
  }), [customers]);
  const quickActions = useMemo(() => {
    const actions = [];
    if (canManageCustomers) {
      actions.push({
        label: showCreateForm ? 'Hide Create Form' : 'Create Customer',
        onClick: () => setShowCreateForm((value) => !value),
      });
    }
    const missingContact = customers.find((customer) => !customer.email || !customer.phone);
    if (missingContact) {
      actions.push({
        label: 'Review Missing Contact',
        onClick: () => setSelectedCustomerId(missingContact.id),
      });
    }
    const missingAddress = customers.find((customer) => !customer.address);
    if (missingAddress) {
      actions.push({
        label: 'Review Missing Address',
        onClick: () => setSelectedCustomerId(missingAddress.id),
      });
    }
    return actions.slice(0, 3);
  }, [canManageCustomers, showCreateForm, customers]);

  const updateSelectedCustomerField = (key, value) => {
    setCustomers((prev) => prev.map((customer) => (
      customer.id === selectedCustomerId ? { ...customer, [key]: value } : customer
    )));
  };

  const handleRefresh = () => {
    setRefreshKey((value) => value + 1);
  };

  const handleSaveCustomer = async () => {
    if (!token || !canManageCustomers || !selectedCustomer) return;
    setSaving(true);
    setError('');
    try {
      const updated = await apiFetch(`/api/customers/${encodeURIComponent(selectedCustomer.id)}`, {
        token,
        method: 'PUT',
        body: {
          name: selectedCustomer.name,
          email: selectedCustomer.email || '',
          phone: selectedCustomer.phone || '',
          address: selectedCustomer.address || '',
        },
      });
      setCustomers((prev) => prev.map((customer) => (
        customer.id === updated.id ? updated : customer
      )));
      pushToast(`Saved ${updated.name}`, 'success');
    } catch (e) {
      setError(e.message || 'Failed to save customer');
      pushToast(e.message || 'Failed to save customer', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCustomer = async (event) => {
    event.preventDefault();
    if (!token || !canManageCustomers) return;
    setSaving(true);
    setError('');
    try {
      const created = await apiFetch('/api/customers', {
        token,
        method: 'POST',
        body: draft,
      });
      setCustomers((prev) => [...prev, created]);
      setSelectedCustomerId(created.id);
      setDraft(makeEmptyDraft());
      closeCreateForm();
      pushToast(`Created ${created.name}`, 'success');
    } catch (e) {
      setError(e.message || 'Failed to create customer');
      pushToast(e.message || 'Failed to create customer', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCustomer = async () => {
    if (!token || !canDeleteCustomers || !selectedCustomer) return;
    if (!window.confirm(`Delete ${selectedCustomer.name}?`)) return;
    setSaving(true);
    setError('');
    try {
      await apiFetch(`/api/customers/${encodeURIComponent(selectedCustomer.id)}`, {
        token,
        method: 'DELETE',
      });
      setCustomers((prev) => prev.filter((customer) => customer.id !== selectedCustomer.id));
      pushToast(`Deleted ${selectedCustomer.name}`, 'success');
    } catch (e) {
      setError(e.message || 'Failed to delete customer');
      pushToast(e.message || 'Failed to delete customer', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="customers-page">
        <Skeleton lines={10} />
      </section>
    );
  }

  return (
    <section className="customers-page">
      <div className="customers-header">
        <div>
          <span className="customers-eyebrow">Relationships</span>
          <h1 className="customers-heading">Customers</h1>
          <p className="customers-subheading">
            Contact records, service accounts, and account details for every customer in the field operation.
          </p>
        </div>
        <div className="customers-header-actions">
          <Button variant="secondary" onClick={handleRefresh}>
            <RefreshIcon />
            Refresh
          </Button>
          {canManageCustomers ? (
            <Button onClick={() => setShowCreateForm((value) => !value)}>
              <PlusIcon />
              New Customer
            </Button>
          ) : null}
        </div>
      </div>

      {error ? <ErrorState message={error} onRetry={loadCustomers} /> : null}

      <Card className="workflow-guide-card" title="What To Do Here">
        <div className="workflow-guide-head">
          <p className="workflow-guide-copy">
            Use Customers to keep service accounts complete, reachable, and ready for dispatch, quoting, and invoice follow-up.
          </p>
          <div className="workflow-guide-actions">
            {quickActions.map((action) => (
              <Button key={action.label} variant="secondary" size="sm" onClick={action.onClick}>
                {action.label}
              </Button>
            ))}
          </div>
        </div>
        <div className="workflow-guide-list">
          <div className="workflow-guide-item">Start by fixing records with missing email, phone, or address details so downstream work stays clean.</div>
          <div className="workflow-guide-item">Use the directory to select one account, then update the detail panel instead of editing from memory.</div>
          <div className="workflow-guide-item">Keep customer records current here before creating jobs, quotes, or invoices tied to that account.</div>
        </div>
      </Card>

      <div className="customers-stats-grid">
        <CustomerStat label="Total" value={stats.total} />
        <CustomerStat label="With Email" value={stats.withEmail} tone="accent" />
        <CustomerStat label="With Phone" value={stats.withPhone} tone="info" />
        <CustomerStat label="With Address" value={stats.withAddress} tone="warning" />
      </div>

      {showCreateForm ? (
        <div ref={createCardRef}>
        <Card className="customers-create-card" title="Create Customer">
          <form className="customers-form-grid" onSubmit={handleCreateCustomer}>
            <CustomerField label="Name">
              <input
                ref={createNameInputRef}
                className="customers-input"
                value={draft.name}
                onChange={(event) => setDraft((prev) => ({ ...prev, name: event.target.value }))}
                placeholder="Acme Facilities"
                required
              />
            </CustomerField>
            <CustomerField label="Email">
              <input
                className="customers-input"
                type="email"
                value={draft.email}
                onChange={(event) => setDraft((prev) => ({ ...prev, email: event.target.value }))}
                placeholder="ops@acme.com"
              />
            </CustomerField>
            <CustomerField label="Phone">
              <input
                className="customers-input"
                value={draft.phone}
                onChange={(event) => setDraft((prev) => ({ ...prev, phone: event.target.value }))}
                placeholder="(555) 123-4567"
              />
            </CustomerField>
            <CustomerField label="Address">
              <input
                className="customers-input"
                value={draft.address}
                onChange={(event) => setDraft((prev) => ({ ...prev, address: event.target.value }))}
                placeholder="1420 Westlake Ave"
              />
            </CustomerField>
            <div className="customers-form-actions">
              <Button variant="ghost" onClick={closeCreateForm}>Cancel</Button>
              <Button type="submit" disabled={saving}>Create Customer</Button>
            </div>
          </form>
        </Card>
        </div>
      ) : null}

      <div className="customers-layout">
        <Card className="customers-list-card" title="Directory">
          <div className="customers-toolbar">
            <input
              className="customers-input customers-search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search by name, email, phone, or address"
            />
          </div>

          {filteredCustomers.length ? (
            <div className="customers-list">
              {filteredCustomers.map((customer) => (
                <CustomerListItem
                  key={customer.id}
                  customer={customer}
                  active={customer.id === selectedCustomerId}
                  onSelect={setSelectedCustomerId}
                />
              ))}
            </div>
          ) : (
            <EmptyState title="No customers match this search" message="Try a wider search or create a new customer record." />
          )}
        </Card>

        <Card className="customers-detail-card" title={selectedCustomer ? selectedCustomer.name : 'Details'}>
          {!selectedCustomer ? (
            <EmptyState title="No customer selected" message="Select a customer from the directory to inspect account details." />
          ) : (
            <div className="customers-detail">
              <div className="customers-hero">
                <div className="customers-avatar customers-avatar--large">
                  {String(selectedCustomer.name || '?').slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <div className="customers-id">{selectedCustomer.id}</div>
                  <h2 className="customers-detail-name">{selectedCustomer.name || 'Unnamed customer'}</h2>
                  <p className="customers-detail-copy">
                    Keep service contacts, account routing, and on-site address details current for smoother dispatching.
                  </p>
                </div>
              </div>

              <div className="customers-detail-grid">
                <CustomerField label="Name">
                  {canManageCustomers ? (
                    <input
                      className="customers-input"
                      value={selectedCustomer.name || ''}
                      onChange={(event) => updateSelectedCustomerField('name', event.target.value)}
                    />
                  ) : (
                    <div className="customers-readout">{selectedCustomer.name || 'No name'}</div>
                  )}
                </CustomerField>
                <CustomerField label="Email">
                  {canManageCustomers ? (
                    <input
                      className="customers-input"
                      type="email"
                      value={selectedCustomer.email || ''}
                      onChange={(event) => updateSelectedCustomerField('email', event.target.value)}
                    />
                  ) : (
                    <div className="customers-readout">{selectedCustomer.email || 'No email'}</div>
                  )}
                </CustomerField>
                <CustomerField label="Phone">
                  {canManageCustomers ? (
                    <input
                      className="customers-input"
                      value={selectedCustomer.phone || ''}
                      onChange={(event) => updateSelectedCustomerField('phone', event.target.value)}
                    />
                  ) : (
                    <div className="customers-readout">{selectedCustomer.phone || 'No phone'}</div>
                  )}
                </CustomerField>
                <CustomerField label="Address">
                  {canManageCustomers ? (
                    <input
                      className="customers-input"
                      value={selectedCustomer.address || ''}
                      onChange={(event) => updateSelectedCustomerField('address', event.target.value)}
                    />
                  ) : (
                    <div className="customers-readout">{selectedCustomer.address || 'No address'}</div>
                  )}
                </CustomerField>
              </div>

              <div className="customers-detail-actions">
                {canManageCustomers ? (
                  <Button onClick={handleSaveCustomer} disabled={saving}>
                    Save Changes
                  </Button>
                ) : null}
                {canDeleteCustomers ? (
                  <Button variant="danger" onClick={handleDeleteCustomer} disabled={saving}>
                    Delete Customer
                  </Button>
                ) : null}
              </div>
            </div>
          )}
        </Card>
      </div>
    </section>
  );
}
